import { useErrorNotification } from '@/hooks/use-error-notification';
import { useNotification } from '@/hooks/use-notification';
import { useDownloadSimulationExcelMismatches } from '@/pages/price-planning/simulation/simulation-queries';
import { formatDateAsDdMmYyyyPrefix } from '@/pages/price-planning/simulation/utils/upload-simulation-helpers';
import { formatDateStringIso, parseDateString } from '@/utils/date-utils';
import {
  Button,
  DatePicker,
  Label,
  Modal,
} from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import { type RefObject, useState } from 'react';

type DownloadMismatchesModalProps = {
  isOpen: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

/** Triggers a browser download for the given blob/file name, then releases the object URL. */
const triggerBrowserDownload = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const DownloadMismatchesModal = ({
  isOpen,
  triggerRef,
  onClose,
}: DownloadMismatchesModalProps) => {
  const showNotification = useNotification();
  const showErrorNotification = useErrorNotification();
  const [modalNode, setModalNode] = useState<HTMLDivElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const { mutate: downloadMismatches, isPending: isDownloading } =
    useDownloadSimulationExcelMismatches((error) =>
      showErrorNotification(error, 'download simulation mismatches'),
    );

  const reset = () => {
    setSelectedDate(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownload = () => {
    if (!selectedDate) return;

    const prefix = formatDateAsDdMmYyyyPrefix(selectedDate);

    downloadMismatches(prefix, {
      onSuccess: ({ blob, fileName }) => {
        triggerBrowserDownload(blob, fileName);
        showNotification({
          type: 'success',
          title: 'Download started',
          description: `${fileName} has been downloaded.`,
        });
        handleClose();
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="medium"
      triggerRef={triggerRef}
      ref={setModalNode}
    >
      <Modal.Title>Download Simulation Mismatches</Modal.Title>
      <Modal.Content style={{ padding: '24px' }}>
        <Flex flexDirection="column" gap="16px">
          <Text className="body-sm-regular">
            Select the date to download the recorded field and key figure
            mismatches for.
          </Text>
          <div>
            <Label>Date</Label>
            <DatePicker
              mode="single"
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              portalTarget={modalNode}
              value={selectedDate ? formatDateStringIso(selectedDate) : null}
              onChange={(_, value) => {
                setSelectedDate(value ? parseDateString(value) : null);
              }}
            />
          </div>
        </Flex>
      </Modal.Content>
      <Modal.Footer>
        <Button
          onClick={handleClose}
          appearance="ghost"
          variant="neutral"
          aria-label="Cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownload}
          appearance="solid"
          variant="primary"
          aria-label="Download"
          loading={isDownloading}
          disabled={!selectedDate}
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DownloadMismatchesModal;
